import { ApolloError } from '@apollo/client';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
} from 'generated/graphql';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';

const getIntegrationData = (
  code: string,
  channel: IntegrationChannelType,
  subDomain: string,
  secret: string,
  direction: IntegrationDirection,
) => {
  switch (channel) {
    case undefined:
      throw Error(`invalid channel: ${channel}`);
    case null:
      throw Error(`invalid channel: ${channel}`);
    case IntegrationChannelType.Slack:
      return {
        slack: {
          authGrant: code,
          redirectURI: `${window.location.origin}/settings/integrations`,
        },
      };
    case IntegrationChannelType.Intercom:
      return {
        intercom: {
          authGrant: code,
        },
      };
    case IntegrationChannelType.ZendeskChat:
      return {
        zendeskChat: {
          accessToken: code,
          subDomain,
        },
      };
    case IntegrationChannelType.Delighted:
      return {
        delighted: {
          apiToken: code,
        },
      };
    case IntegrationChannelType.Freshchat:
      return {
        freshchat: {
          accessToken: code,
        },
      };
    case IntegrationChannelType.Webhook:
      return {
        webhook: {
          displayName: subDomain,
        },
      };
    case IntegrationChannelType.Email:
      if (direction === IntegrationDirection.Inbound) {
        return {
          emailInbound: {
            displayName: code.split(',')[0].trim(),
          },
        };
      }

      return {
        emails: {
          toEmails: code
            .split(',')
            .slice(1)
            .map((email) => email.trim()),
          displayName: code.split(',')[0].trim(),
        },
      };
    case IntegrationChannelType.FileUpload:
      return {
        fileUpload: {
          fileSource: code,
        },
      };
    case IntegrationChannelType.StellaConnect:
      return {
        stellaConnect: {
          apiKey: code,
          secretKey: secret,
        },
      };
    case IntegrationChannelType.Front:
      return {
        front: {
          accessToken: code,
        },
      };
    case IntegrationChannelType.Amplitude:
      return {
        amplitude: {},
      };
    case IntegrationChannelType.MixPanel:
      return {
        mixpanel: {},
      };
    case IntegrationChannelType.FreshDesk:
      return {
        freshDesk: { domain: subDomain, apiKey: code },
      };
    case IntegrationChannelType.Appstore:
      return {
        appstore: {
          appID: '',
          appName: '',
          appURL: code,
        },
      };
    case IntegrationChannelType.Playstore:
      return {
        playstore: {
          appID: '',
          appURL: code,
        },
      };
    case IntegrationChannelType.Twitter:
      return {
        twitter: {
          twitterHandle: code,
        },
      };
    case IntegrationChannelType.Kustomer:
      return {
        kustomer: {
          accessToken: code,
        },
      };
    case IntegrationChannelType.G2:
      return {
        g2: {
          slug: code,
        },
      };
    case IntegrationChannelType.Salesforce:
      return {
        salesforce: {
          authGrant: code,
          redirectURI: `${window.location.origin}/settings/integrations`,
        },
      };
    case IntegrationChannelType.InApp:
      return {
        inApp: {},
      };

    default:
      throw Error(`invalid channel: ${channel}`);
  }
};

const useCreateIntegrationFormData = ({
  tenantID,
  userID,
  state,
  // TODO: Change to JSON
  code,
  direction,
  subDomain = '',
  secret = '',
  onError,
  onCompleted,
  integrationType,
}: {
  tenantID: string;
  userID: string;
  state: IntegrationChannelType;
  code: string;
  direction: IntegrationDirection;
  subDomain?: string;
  secret?: string;
  onError?: (error: ApolloError) => void;
  onCompleted?: () => void;
  integrationType: IntegrationType;
}) => {
  const isAddIntegrationDrawerOpen = useIntegrationsStore(
    ({ view }) => view.isAddDrawerOpen,
  );
  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const { status, error, createIntegration } = useCreateIntegrationEdgeServer({
    tenantID,
    direction: !direction ? IntegrationDirection.Inbound : direction,
    channel: state,
    integrationType,
    createdByUser: userID,
    inputData: getIntegrationData(code, state, subDomain, secret, direction),
    onError,
    onCompleted: () => {
      if (isAddIntegrationDrawerOpen) {
        dispatch({
          type: IntegrationsActionType.SET_VIEW,
          payload: {
            isAddDrawerOpen: false,
          },
        });
      }
      onCompleted?.();
    },
  });

  return {
    loading: IsLoading(status),
    error,
    createIntegration,
  };
};
export default useCreateIntegrationFormData;
